import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Inventory } from "../../../../../api";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { ACTIONS } from "../../../../../utils/constants";

import styles from "./InventoryEntryPage.module.scss";
import {
  INITIAL_VALUES,
  createEntryItem,
  flattenProducts,
  formatCurrency,
  formatNumber,
  validationSchema,
} from "./InventoryEntryPage.form";

function getExistencesResponse(result) {
  const response = result?.existences || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response;
  }

  if (Array.isArray(response)) {
    return {
      docs: response,
      totalDocs: response.length,
    };
  }

  return {
    docs: [],
    totalDocs: 0,
  };
}

function getErrorMessage(error) {
  if (!error) {
    return "No fue posible completar la operación.";
  }

  if (typeof error === "string") {
    return error;
  }

  return error.msg || error.message || "No fue posible completar la operación.";
}

function getItemError(errors, index, fieldName) {
  return errors?.items?.[index]?.[fieldName] || "";
}

function LocationVariantAutoAdder({
  selectedVariant,
  items,
  setFieldValue,
}) {
  useEffect(() => {
    if (!selectedVariant) {
      return;
    }

    const alreadyAdded = items.some(
      (item) =>
        String(item.product) === String(selectedVariant.productId) &&
        String(item.variantId) === String(selectedVariant.variantId)
    );

    if (!alreadyAdded) {
      setFieldValue("items", [
        ...items,
        createEntryItem(selectedVariant),
      ]);
    }
  }, [selectedVariant, items, setFieldValue]);

  return null;
}

export function InventoryEntryPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { hasAction } = usePermissions();
  // Entrada de inventario es un permiso propio y específico (ENTRY), no el
  // genérico "ADD" -- el botón de la lista ya se oculta sin este permiso,
  // pero esta página se puede alcanzar por URL directa, así que el
  // envío del formulario también se bloquea acá.
  const canEntry = hasAction("inventory", ACTIONS.ENTRY);

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [availableVariants, setAvailableVariants] = useState([]);
  // Filtro para mostrar solo variantes sin stock: facilita encontrar qué
  // ingresar cuando ya se sabe que se va a registrar mercancía para
  // productos agotados, sin tener que revisar uno por uno en la lista.
  const [onlyOutOfStock, setOnlyOutOfStock] = useState(false);

  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [serverError, setServerError] = useState("");

  const initialSelectedProduct = location.state?.productId || "";
  const initialSelectedVariant = location.state?.variantId || "";

  const loadVariants = useCallback(async () => {
    try {
      setIsLoadingVariants(true);
      setServerError("");

      const inventoryController = new Inventory();

      const result = await inventoryController.getExistences(1, 100, {
        search: appliedSearch || undefined,
      });

      const response = getExistencesResponse(result);

      setAvailableVariants(flattenProducts(response.docs || []));
    } catch (errorResponse) {
      console.error("loadVariants error:", errorResponse);

      setAvailableVariants([]);
      setServerError(
        errorResponse?.msg ||
          errorResponse?.message ||
          "No fue posible cargar productos y variantes."
      );
    } finally {
      setIsLoadingVariants(false);
    }
  }, [appliedSearch]);

  useEffect(() => {
    loadVariants();
  }, [loadVariants]);

  const selectedLocationVariant = useMemo(() => {
    if (!initialSelectedProduct || !initialSelectedVariant) {
      return null;
    }

    return availableVariants.find(
      (variant) =>
        String(variant.productId) === String(initialSelectedProduct) &&
        String(variant.variantId) === String(initialSelectedVariant)
    );
  }, [
    availableVariants,
    initialSelectedProduct,
    initialSelectedVariant,
  ]);

  // Solo afecta lo que se muestra en los resultados de búsqueda; el
  // detalle de la entrada (values.items) no se ve afectado por este
  // filtro una vez que una variante ya fue agregada.
  const visibleVariants = useMemo(() => {
    if (!onlyOutOfStock) {
      return availableVariants;
    }

    return availableVariants.filter((variant) => variant.stock === 0);
  }, [availableVariants, onlyOutOfStock]);

  function handleSearchSubmit() {
    setAppliedSearch(searchValue.trim());
  }

  function handleSearchInputKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchSubmit();
    }
  }

  function handleCancel() {
    navigate("/admin/inventory");
  }

  async function handleSubmit(values, { setSubmitting, setErrors }) {
    try {
      setServerError("");

      const inventoryController = new Inventory();

      await inventoryController.createEntry({
        reference: values.reference,
        notes: values.notes,
        items: values.items.map((item) => ({
          product: item.product,
          variantId: item.variantId,
          quantity: Number(item.quantity),
          unitCost:
            item.unitCost === "" || item.unitCost === null
              ? null
              : Number(item.unitCost),
        })),
      });

      navigate("/admin/inventory", {
        state: {
          success: "Entrada de inventario registrada correctamente.",
        },
      });
    } catch (errorResponse) {
      console.error("createEntry error:", errorResponse);

      if (errorResponse?.errors) {
        setErrors(errorResponse.errors);
      }

      setServerError(getErrorMessage(errorResponse));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveDraft(values) {
    try {
      setIsSavingDraft(true);

      window.localStorage.setItem(
        "inventory-entry-draft",
        JSON.stringify({
          reference: values.reference,
          notes: values.notes,
          items: values.items,
          savedAt: new Date().toISOString(),
        })
      );

      setServerError("");
    } catch (error) {
      console.error("handleSaveDraft error:", error);

      setServerError(
        "No fue posible guardar el borrador localmente."
      );
    } finally {
      setIsSavingDraft(false);
    }
  }

  return (
    <section className={styles.InventoryEntryPage}>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          setFieldValue,
          isSubmitting,
        }) => {
          const totalItems = values.items.length;

          const totalQuantity = values.items.reduce(
            (total, item) => total + (Number(item.quantity) || 0),
            0
          );

          const totalValue = values.items.reduce(
            (total, item) =>
              total +
              (Number(item.quantity) || 0) *
                (Number(item.unitCost) || 0),
            0
          );

          function handleAddVariant(variant) {
            const alreadyAdded = values.items.some(
              (item) =>
                String(item.product) === String(variant.productId) &&
                String(item.variantId) === String(variant.variantId)
            );

            if (alreadyAdded) {
              setServerError(
                "Esta variante ya fue agregada al detalle de la entrada."
              );

              return;
            }

            setFieldValue("items", [
              ...values.items,
              createEntryItem(variant),
            ]);

            setServerError("");
          }

          function handleRemoveItem(localId) {
            setFieldValue(
              "items",
              values.items.filter((item) => item.localId !== localId)
            );
          }

          return (
            <>
              <LocationVariantAutoAdder
                selectedVariant={selectedLocationVariant}
                items={values.items}
                setFieldValue={setFieldValue}
              />

              <header className={styles.PageHeader}>
                <div className={styles.TitleSection}>
                  <Link
                    to="/admin/inventory"
                    className={styles.BackButton}
                    aria-label="Volver a existencias"
                  >
                    ←
                  </Link>

                  <div>
                    <p className={styles.Breadcrumb}>
                      Inventario / Ingreso de inventario
                    </p>

                    <h1>Ingreso de inventario</h1>

                    <p className={styles.Subtitle}>
                      Registra una entrada de mercancía y actualiza las
                      existencias de las variantes seleccionadas.
                    </p>
                  </div>
                </div>

                <div className={styles.HeaderActions}>
                  <button
                    type="button"
                    className={styles.CancelButton}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={styles.DraftButton}
                    onClick={() => handleSaveDraft(values)}
                    disabled={isSubmitting || isSavingDraft}
                  >
                    {isSavingDraft
                      ? "Guardando..."
                      : "Guardar borrador"}
                  </button>

                  <button
                    type="submit"
                    form="inventory-entry-form"
                    className={styles.ConfirmButton}
                    disabled={isSubmitting || !canEntry}
                  >
                    {isSubmitting
                      ? "Confirmando..."
                      : "Confirmar entrada"}
                  </button>
                </div>
              </header>

              {!canEntry && (
                <div className={styles.ValidationMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <span>
                    No tienes permiso para registrar entradas de inventario.
                  </span>
                </div>
              )}

              {serverError && (
                <div className={styles.ServerErrorMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <span>{serverError}</span>
                </div>
              )}

              {Object.keys(errors).length > 0 && (
                <div className={styles.ValidationMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <div>
                    <strong>Revisa los datos de la entrada.</strong>

                    <span>
                      Corrige los campos marcados antes de confirmar el
                      movimiento.
                    </span>
                  </div>
                </div>
              )}

              <Form
                id="inventory-entry-form"
                className={styles.FormContent}
              >
                <div className={styles.FormColumns}>
                  <section className={styles.GeneralInformationCard}>
                    <header className={styles.SectionHeader}>
                      <div>
                        <h2>Información general</h2>

                        <p>
                          Registra la referencia y notas de la recepción.
                        </p>
                      </div>
                    </header>

                    <div className={styles.GeneralInformationContent}>
                      <div className={styles.FormField}>
                        <label htmlFor="reference">
                          Referencia / Factura
                        </label>

                        <Field
                          id="reference"
                          name="reference"
                          type="text"
                          placeholder="Ej. OC-2025-008541"
                          disabled={isSubmitting}
                        />

                        <ErrorMessage
                          name="reference"
                          component="span"
                          className={styles.FieldError}
                        />
                      </div>

                      <div className={styles.FormField}>
                        <label htmlFor="notes">Notas</label>

                        <Field
                          as="textarea"
                          id="notes"
                          name="notes"
                          placeholder="Ej. Ingreso de colección Primavera-Verano 2025."
                          maxLength="1000"
                          disabled={isSubmitting}
                        />

                        <div className={styles.TextAreaFooter}>
                          <ErrorMessage
                            name="notes"
                            component="span"
                            className={styles.FieldError}
                          />

                          <span>{values.notes.length} / 1000</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className={styles.ProductSearchCard}>
                    <header className={styles.SectionHeader}>
                      <div>
                        <h2>Buscar producto / variante</h2>

                        <p>
                          Busca por nombre, SKU o código para agregar al
                          detalle.
                        </p>
                      </div>
                    </header>

                    <div className={styles.ProductSearchContent}>
                      <div className={styles.SearchForm}>
                        <input
                          type="search"
                          value={searchValue}
                          onChange={(event) =>
                            setSearchValue(event.target.value)
                          }
                          onKeyDown={handleSearchInputKeyDown}
                          placeholder="Buscar producto, SKU o código de barras..."
                          aria-label="Buscar producto o variante"
                          disabled={isLoadingVariants || isSubmitting}
                        />

                        <button
                          type="button"
                          onClick={handleSearchSubmit}
                          aria-label="Buscar"
                          disabled={isLoadingVariants || isSubmitting}
                        >
                          ⌕
                        </button>
                      </div>

                      <label className={styles.StockFilterToggle}>
                        <input
                          type="checkbox"
                          checked={onlyOutOfStock}
                          onChange={(event) =>
                            setOnlyOutOfStock(event.target.checked)
                          }
                          disabled={isLoadingVariants || isSubmitting}
                        />
                        Mostrar solo variantes sin stock
                      </label>

                      {isLoadingVariants ? (
                        <div className={styles.SearchState}>
                          Cargando variantes...
                        </div>
                      ) : visibleVariants.length === 0 ? (
                        <div className={styles.SearchState}>
                          {onlyOutOfStock
                            ? "No hay variantes sin stock con este criterio de búsqueda."
                            : "No se encontraron productos o variantes."}
                        </div>
                      ) : (
                        <div className={styles.SearchResults}>
                          {visibleVariants.map((variant) => {
                            const alreadyAdded = values.items.some(
                              (item) =>
                                String(item.product) ===
                                  String(variant.productId) &&
                                String(item.variantId) ===
                                  String(variant.variantId)
                            );

                            return (
                              <article
                                key={`${variant.productId}-${variant.variantId}`}
                                className={styles.ProductResult}
                              >
                                {variant.productImage ? (
                                  <img
                                    src={variant.productImage}
                                    alt={variant.productName}
                                  />
                                ) : (
                                  <span
                                    className={
                                      styles.ProductImagePlaceholder
                                    }
                                    aria-hidden="true"
                                  >
                                    ▣
                                  </span>
                                )}

                                <div className={styles.ProductResultInfo}>
                                  <strong title={variant.productName}>
                                    {variant.productName}
                                  </strong>

                                  <span
                                    className={styles.ResultSku}
                                    title={`SKU: ${variant.sku || "Sin SKU"}`}
                                  >
                                    SKU: {variant.sku || "Sin SKU"}
                                  </span>

                                  <span
                                    className={styles.ResultVariant}
                                    title={variant.variantLabel}
                                  >
                                    {variant.variantLabel}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  className={styles.AddProductButton}
                                  onClick={() => handleAddVariant(variant)}
                                  disabled={isSubmitting || alreadyAdded}
                                >
                                  {alreadyAdded ? "Agregado" : "Agregar"}
                                </button>
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <section className={styles.DetailCard}>
                  <header className={styles.SectionHeader}>
                    <div>
                      <h2>Detalle de productos</h2>

                      <p>
                        Define cantidad y costo unitario para cada variante.
                      </p>
                    </div>

                    <span className={styles.ItemsCounter}>
                      {totalItems}{" "}
                      {totalItems === 1 ? "producto" : "productos"}
                    </span>
                  </header>

                  {errors.items && typeof errors.items === "string" && (
                    <div className={styles.DetailValidationMessage}>
                      {errors.items}
                    </div>
                  )}

                  {values.items.length === 0 ? (
                    <div className={styles.EmptyDetailState}>
                      <p>
                        Busca y agrega al menos un producto o variante para
                        registrar la entrada.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.TableContainer}>
                        <table className={styles.Table}>
                          <thead>
                            <tr>
                              <th>Producto</th>
                              <th>SKU</th>
                              <th>Variante</th>
                              <th>Disponible</th>
                              <th>Cantidad</th>
                              <th>Costo unitario</th>
                              <th>Total</th>
                              <th aria-label="Acciones" />
                            </tr>
                          </thead>

                          <tbody>
                            {values.items.map((item, index) => {
                              const quantityError = getItemError(
                                errors,
                                index,
                                "quantity"
                              );

                              const costError = getItemError(
                                errors,
                                index,
                                "unitCost"
                              );

                              const itemTotal =
                                (Number(item.quantity) || 0) *
                                (Number(item.unitCost) || 0);

                              return (
                                <tr key={item.localId}>
                                  <td>
                                    <div className={styles.ProductCell}>
                                      {item.productImage ? (
                                        <img
                                          src={item.productImage}
                                          alt={item.productName}
                                        />
                                      ) : (
                                        <span
                                          className={
                                            styles.ProductImagePlaceholder
                                          }
                                          aria-hidden="true"
                                        >
                                          ▣
                                        </span>
                                      )}

                                      <div>
                                        <strong title={item.productName}>
                                          {item.productName}
                                        </strong>

                                        <span
                                          title={
                                            item.productCode || "Sin código"
                                          }
                                        >
                                          {item.productCode ||
                                            "Sin código"}
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  <td>
                                    <span
                                      className={styles.SkuCode}
                                      title={item.sku || "—"}
                                    >
                                      {item.sku || "—"}
                                    </span>
                                  </td>

                                  <td>
                                    <span
                                      className={styles.VariantLabel}
                                      title={item.variantLabel}
                                    >
                                      {item.variantLabel}
                                    </span>
                                  </td>

                                  <td>
                                    <span className={styles.AvailableStock}>
                                      {formatNumber(item.availableStock)}
                                    </span>
                                  </td>

                                  <td>
                                    <Field
                                      name={`items[${index}].quantity`}
                                      type="number"
                                      min="1"
                                      step="1"
                                      className={
                                        quantityError
                                          ? styles.error
                                          : ""
                                      }
                                      disabled={isSubmitting}
                                    />

                                    {quantityError && (
                                      <span className={styles.FieldError}>
                                        {quantityError}
                                      </span>
                                    )}
                                  </td>

                                  <td>
                                    <Field
                                      name={`items[${index}].unitCost`}
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      placeholder="0.00"
                                      className={
                                        costError ? styles.error : ""
                                      }
                                      disabled={isSubmitting}
                                    />

                                    {costError && (
                                      <span className={styles.FieldError}>
                                        {costError}
                                      </span>
                                    )}
                                  </td>

                                  <td>
                                    <strong className={styles.TotalValue}>
                                      {formatCurrency(itemTotal)}
                                    </strong>
                                  </td>

                                  <td>
                                    <button
                                      type="button"
                                      className={styles.RemoveItemButton}
                                      onClick={() =>
                                        handleRemoveItem(item.localId)
                                      }
                                      disabled={isSubmitting}
                                      aria-label={`Eliminar ${item.productName}`}
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <footer className={styles.DetailSummary}>
                        <div>
                          <span>Total ítems</span>
                          <strong>{totalItems}</strong>
                        </div>

                        <div>
                          <span>Total unidades</span>
                          <strong>{formatNumber(totalQuantity)}</strong>
                        </div>

                        <div className={styles.GrandTotal}>
                          <span>Total entrada</span>
                          <strong>{formatCurrency(totalValue)}</strong>
                        </div>
                      </footer>
                    </>
                  )}
                </section>
              </Form>
            </>
          );
        }}
      </Formik>
    </section>
  );
}